import { useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { X, Upload, Download, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";

// Minimal CSV parser: handles quoted fields, escaped quotes, and commas inside quotes.
// Returns { headers: string[], rows: string[][] }.
function parseCSV(text) {
  const rows = [];
  let cur = [];
  let field = "";
  let inQuotes = false;
  const flushField = () => { cur.push(field); field = ""; };
  const flushRow = () => { rows.push(cur); cur = []; };
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ",") flushField();
      else if (c === "\n") { flushField(); flushRow(); }
      else if (c === "\r") { /* skip */ }
      else field += c;
    }
  }
  if (field.length || cur.length) { flushField(); flushRow(); }
  const nonEmpty = rows.filter((r) => r.some((v) => v && v.trim().length));
  if (!nonEmpty.length) return { headers: [], rows: [] };
  const [headers, ...body] = nonEmpty;
  return { headers: headers.map((h) => h.trim()), rows: body };
}

// Guess which CSV column matches each of our lead fields.
// Ranked by common Apollo / Instantly / Smartlead / Hubspot export headers.
const HEURISTICS = {
  name:     ["company", "company name", "business name", "business", "account name", "name", "organization", "organization name"],
  email:    ["email", "email address", "work email", "primary email", "contact email"],
  phone:    ["phone", "company phone", "corporate phone", "direct phone", "mobile phone", "phone number", "work phone", "cell"],
  city:     ["city", "company city", "business city", "town"],
  state:    ["state", "company state", "business state", "province", "region"],
  industry: ["industry", "company industry", "vertical", "sector"],
  notes:    ["title", "job title", "notes", "message", "reply", "reply body", "description"],
  contact_name: ["contact name", "full name", "first name", "person", "lead name"],
};

function guessMapping(headers) {
  const lower = headers.map((h) => (h || "").toLowerCase().trim());
  const out = {};
  Object.entries(HEURISTICS).forEach(([field, guesses]) => {
    for (const g of guesses) {
      const idx = lower.indexOf(g);
      if (idx !== -1) { out[field] = headers[idx]; return; }
    }
    out[field] = ""; // no guess
  });
  return out;
}

const FIELDS = [
  { key: "name",         label: "Business name",   required: true,  help: "The company. If your CSV has a person's name, pick 'Company' / 'Business'." },
  { key: "email",        label: "Email",           required: false, help: "" },
  { key: "phone",        label: "Phone",           required: false, help: "" },
  { key: "city",         label: "City",            required: true,  help: "Falls back to the default below if missing on a row." },
  { key: "state",        label: "State",           required: true,  help: "Falls back to the default below if missing on a row." },
  { key: "industry",     label: "Industry",        required: true,  help: "Falls back to the default below if missing on a row." },
  { key: "notes",        label: "Notes",           required: false, help: "Great for pasting job title / reply body / anything extra." },
  { key: "contact_name", label: "Contact person",  required: false, help: "Prefixed into Notes automatically." },
];

const REQUIRED_KEYS = FIELDS.filter((f) => f.required).map((f) => f.key);

export default function LeadsImportModal({ onClose, onImported }) {
  const [file, setFile] = useState(null);
  const [parsed, setParsed] = useState(null); // { headers, rows }
  const [mapping, setMapping] = useState({});
  const [defaults, setDefaults] = useState({ industry: "", city: "", state: "", source: "Apollo" });
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0, ok: 0, skipped: 0, errors: [] });
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (f) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".csv")) return toast.error("Please upload a .csv file.");
    if (f.size > 5 * 1024 * 1024) return toast.error("File too big — keep it under 5 MB (~10k rows).");
    setFile(f);
    const text = await f.text();
    const p = parseCSV(text);
    if (!p.headers.length) return toast.error("Could not read that CSV — is it empty?");
    setParsed(p);
    setMapping(guessMapping(p.headers));
  };

  const missingRequired = useMemo(() => {
    return REQUIRED_KEYS.filter((k) => !mapping[k] && !defaults[k]);
  }, [mapping, defaults]);

  const previewRows = useMemo(() => {
    if (!parsed) return [];
    const idx = (colName) => parsed.headers.indexOf(colName);
    return parsed.rows.slice(0, 5).map((r) => ({
      name: mapping.name ? r[idx(mapping.name)] : "",
      email: mapping.email ? r[idx(mapping.email)] : "",
      phone: mapping.phone ? r[idx(mapping.phone)] : "",
      city: (mapping.city ? r[idx(mapping.city)] : "") || defaults.city,
      state: (mapping.state ? r[idx(mapping.state)] : "") || defaults.state,
      industry: (mapping.industry ? r[idx(mapping.industry)] : "") || defaults.industry,
    }));
  }, [parsed, mapping, defaults]);

  const runImport = async () => {
    if (!parsed) return;
    if (missingRequired.length) {
      return toast.error(`Fill in defaults for: ${missingRequired.join(", ")}`);
    }
    setImporting(true);
    setProgress({ done: 0, total: parsed.rows.length, ok: 0, skipped: 0, errors: [] });
    const idx = (colName) => parsed.headers.indexOf(colName);
    const pick = (row, field) => (mapping[field] ? (row[idx(mapping[field])] || "").trim() : "");

    let ok = 0, skipped = 0;
    const errors = [];
    for (let i = 0; i < parsed.rows.length; i++) {
      const r = parsed.rows[i];
      const name = pick(r, "name");
      if (!name) { skipped++; setProgress((p) => ({ ...p, done: i + 1, skipped })); continue; }
      const contactPerson = pick(r, "contact_name");
      const rawNotes = pick(r, "notes");
      const notesParts = [
        contactPerson ? `Contact: ${contactPerson}` : "",
        `Source: ${defaults.source || "Apollo"}`,
        rawNotes || "",
      ].filter(Boolean);
      const payload = {
        name,
        industry: pick(r, "industry") || defaults.industry,
        city: pick(r, "city") || defaults.city,
        state: pick(r, "state") || defaults.state,
        email: pick(r, "email"),
        phone: pick(r, "phone"),
        notes: notesParts.join(" · "),
        has_website: false,
      };
      try {
        await api.post("/leads/import", payload);
        ok++;
      } catch (e) {
        errors.push({ row: i + 2, name, error: formatApiError(e) }); // +2 = 1-index + header row
      }
      setProgress({ done: i + 1, total: parsed.rows.length, ok, skipped, errors });
    }
    setImporting(false);
    toast.success(`Imported ${ok} lead${ok === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped (no name)` : ""}${errors.length ? ` · ${errors.length} failed` : ""}.`);
    if (ok > 0) onImported?.();
  };

  const done = progress.total > 0 && progress.done === progress.total && !importing;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="leads-import-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-3xl border border-neutral-900 shadow-2xl max-h-[92vh] overflow-y-auto"
      >
        <div className="bg-neutral-900 text-white px-6 py-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-amber-300">Import leads · CSV</div>
            <div className="text-xl font-bold mt-1">Drop your Apollo export here</div>
            <div className="text-xs text-neutral-400 mt-0.5">
              Works with any CSV — Apollo, Instantly, Smartlead, HubSpot, Google Sheets exports.
            </div>
          </div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="leads-import-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Step 1 — File drop */}
        {!parsed && (
          <div className="p-8">
            <label
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                const f = e.dataTransfer.files?.[0];
                handleFile(f);
              }}
              className={`block border-2 border-dashed p-12 text-center cursor-pointer transition ${dragOver ? "border-coral bg-coral/5" : "border-neutral-300 bg-neutral-50 hover:bg-neutral-100"}`}
              data-testid="leads-import-dropzone"
            >
              <Upload className="w-8 h-8 mx-auto text-neutral-400 mb-3" />
              <div className="font-display text-xl mb-1">Drag your CSV here</div>
              <div className="text-sm text-neutral-500 mb-4">or click to browse</div>
              <input
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
                data-testid="leads-import-file-input"
              />
              <div className="inline-block lf-btn-outline text-xs px-4 py-2">Choose file</div>
              <div className="text-xs text-neutral-400 mt-4">Max 5 MB · ~10,000 rows</div>
            </label>

            <div className="mt-6 text-xs text-neutral-500">
              <div className="font-mono uppercase tracking-widest text-neutral-400 mb-2">How to export from Apollo</div>
              <ol className="list-decimal pl-5 space-y-1">
                <li>Apollo → <strong>People</strong> tab → apply your filter (e.g. "Replied this week")</li>
                <li>Top-right → <strong>⋯ More</strong> → <strong>Export CSV</strong></li>
                <li>Wait for the email from Apollo with your download link (usually &lt;1 min)</li>
                <li>Download the CSV → drag it into the box above</li>
              </ol>
            </div>
          </div>
        )}

        {/* Step 2 — Map + defaults */}
        {parsed && !done && (
          <div className="p-6 space-y-5">
            <div className="flex items-center justify-between border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                <span className="font-medium">{file?.name}</span>
                <span className="text-neutral-500 text-xs">· {parsed.rows.length} rows · {parsed.headers.length} columns</span>
              </div>
              <button onClick={() => { setParsed(null); setFile(null); }} className="text-xs text-neutral-500 hover:text-neutral-900">Change file</button>
            </div>

            {/* Column mapping */}
            <div>
              <div className="font-mono uppercase tracking-widest text-xs text-neutral-500 mb-2">1 · Match CSV columns to lead fields</div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {FIELDS.map((f) => (
                  <div key={f.key} className="border border-neutral-200 bg-white px-3 py-2">
                    <div className="text-xs text-neutral-500 mb-1 flex items-center gap-2">
                      {f.label}
                      {f.required && <span className="text-rose-600">*</span>}
                      {mapping[f.key] && <CheckCircle2 className="w-3 h-3 text-emerald-600" />}
                    </div>
                    <select
                      value={mapping[f.key] || ""}
                      onChange={(e) => setMapping((m) => ({ ...m, [f.key]: e.target.value }))}
                      className="w-full text-sm border border-neutral-300 px-2 py-1.5 focus:outline-none focus:border-black"
                      data-testid={`leads-import-map-${f.key}`}
                    >
                      <option value="">— not in this CSV —</option>
                      {parsed.headers.map((h) => <option key={h} value={h}>{h}</option>)}
                    </select>
                    {f.help && <div className="text-[11px] text-neutral-400 mt-1">{f.help}</div>}
                  </div>
                ))}
              </div>
            </div>

            {/* Defaults */}
            <div>
              <div className="font-mono uppercase tracking-widest text-xs text-neutral-500 mb-2">2 · Default values (used when a row is missing that field)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <DefaultInput label="Industry *" value={defaults.industry} onChange={(v) => setDefaults((d) => ({ ...d, industry: v }))} placeholder="e.g. HVAC" testId="leads-import-default-industry" />
                <DefaultInput label="City *"     value={defaults.city}     onChange={(v) => setDefaults((d) => ({ ...d, city: v }))} placeholder="e.g. Austin" testId="leads-import-default-city" />
                <DefaultInput label="State *"    value={defaults.state}    onChange={(v) => setDefaults((d) => ({ ...d, state: v }))} placeholder="e.g. TX" testId="leads-import-default-state" />
                <DefaultInput label="Source tag" value={defaults.source}   onChange={(v) => setDefaults((d) => ({ ...d, source: v }))} placeholder="Apollo" testId="leads-import-default-source" />
              </div>
              {missingRequired.length > 0 && (
                <div className="mt-2 flex items-center gap-2 text-xs text-rose-700">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Set a default for: {missingRequired.join(", ")} (or map a CSV column above).
                </div>
              )}
            </div>

            {/* Preview */}
            <div>
              <div className="font-mono uppercase tracking-widest text-xs text-neutral-500 mb-2">3 · Preview (first 5 rows)</div>
              <div className="border border-neutral-200 overflow-x-auto max-h-56">
                <table className="w-full text-xs">
                  <thead className="bg-neutral-50 text-neutral-600 uppercase tracking-widest text-[10px] font-mono">
                    <tr>
                      <th className="text-left px-2 py-1.5">Business name</th>
                      <th className="text-left px-2 py-1.5">Email</th>
                      <th className="text-left px-2 py-1.5">Phone</th>
                      <th className="text-left px-2 py-1.5">City / State</th>
                      <th className="text-left px-2 py-1.5">Industry</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewRows.map((r, i) => (
                      <tr key={i} className="border-t border-neutral-100">
                        <td className="px-2 py-1.5 font-medium">{r.name || <span className="text-rose-500">— missing —</span>}</td>
                        <td className="px-2 py-1.5 font-mono">{r.email || <span className="text-neutral-300">—</span>}</td>
                        <td className="px-2 py-1.5 font-mono">{r.phone || <span className="text-neutral-300">—</span>}</td>
                        <td className="px-2 py-1.5">{r.city || <span className="text-neutral-300">—</span>}, {r.state || <span className="text-neutral-300">—</span>}</td>
                        <td className="px-2 py-1.5">{r.industry || <span className="text-neutral-300">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Progress + import */}
            {importing && (
              <div className="border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-900 flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Importing {progress.done} / {progress.total}… ({progress.ok} added, {progress.skipped} skipped)
              </div>
            )}

            <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
              <div className="text-xs text-neutral-500">
                Rows: <strong>{parsed.rows.length}</strong>
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={onClose} className="lf-btn-outline text-sm">Cancel</button>
                <button
                  type="button"
                  onClick={runImport}
                  disabled={importing || missingRequired.length > 0}
                  className="lf-btn-primary text-sm inline-flex items-center gap-2"
                  data-testid="leads-import-run"
                >
                  {importing ? <><Loader2 className="w-4 h-4 animate-spin" /> Importing…</> : <><Download className="w-4 h-4" /> Import {parsed.rows.length} lead{parsed.rows.length === 1 ? "" : "s"}</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Step 3 — Done */}
        {done && (
          <div className="p-6" data-testid="leads-import-done">
            <div className="border border-emerald-300 bg-emerald-50 p-4 mb-4">
              <div className="flex items-center gap-2 text-emerald-800 font-semibold">
                <CheckCircle2 className="w-5 h-5" /> Import complete
              </div>
              <div className="text-sm text-emerald-900 mt-2">
                <strong>{progress.ok}</strong> lead{progress.ok === 1 ? "" : "s"} added to your pipeline · <strong>{progress.skipped}</strong> skipped (no business name) · <strong>{progress.errors.length}</strong> failed
              </div>
            </div>
            {progress.errors.length > 0 && (
              <div className="border border-rose-200 bg-rose-50 p-3 text-xs text-rose-900 max-h-40 overflow-y-auto mb-4">
                <div className="font-mono uppercase tracking-widest text-rose-700 mb-1">Errors</div>
                <ul className="list-disc pl-4 space-y-0.5">
                  {progress.errors.slice(0, 20).map((e, i) => (
                    <li key={i}>Row {e.row} ({e.name || "no name"}): {e.error}</li>
                  ))}
                  {progress.errors.length > 20 && <li>…and {progress.errors.length - 20} more.</li>}
                </ul>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button onClick={() => { setParsed(null); setFile(null); setProgress({ done: 0, total: 0, ok: 0, skipped: 0, errors: [] }); }} className="lf-btn-outline text-sm">Import another</button>
              <button onClick={onClose} className="lf-btn-primary text-sm" data-testid="leads-import-close-done">Done</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DefaultInput({ label, value, onChange, placeholder, testId }) {
  return (
    <label className="block">
      <div className="text-xs text-neutral-500 mb-1">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full text-sm border border-neutral-300 px-2 py-1.5 focus:outline-none focus:border-black"
        data-testid={testId}
      />
    </label>
  );
}
